import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { HelpHint } from "@/components/HelpHint";

const WEEKDAYS = [
  { key: "mon", label: "Mon" },
  { key: "tue", label: "Tue" },
  { key: "wed", label: "Wed" },
  { key: "thu", label: "Thu" },
  { key: "fri", label: "Fri" },
  { key: "sat", label: "Sat" },
  { key: "sun", label: "Sun" },
];

// Settings -> Business Rules tab. Extracted from Settings.js (props-driven,
// no behavior change).
export const BusinessRulesSection = ({ businessRules, onChange, onToggleBusinessDay, onSave, saving }) => (
  <div className="space-y-6" data-testid="business-rules-section">
    <div>
      <div className="flex items-center gap-1.5">
        <h2 className="font-heading text-lg font-semibold">Cancellation Policy</h2>
        <HelpHint text="If a lesson is cancelled inside this window, DriveDesk Hub automatically adds a pending fee to the student's account and notifies everyone involved." testId="cancellation-policy-help" />
      </div>
      <div className="grid sm:grid-cols-2 gap-3 max-w-md mt-3">
        <div className="space-y-1.5">
          <Label>Cancellation window (hours before lesson)</Label>
          <Input type="number" min={0} value={businessRules.cancellation_window_hours}
            onChange={(e) => onChange({ ...businessRules, cancellation_window_hours: e.target.value })}
            data-testid="cancellation-window-input" />
        </div>
        <div className="space-y-1.5">
          <Label>Cancellation fee ($)</Label>
          <Input type="number" min={0} step="0.01" value={businessRules.cancellation_fee_amount}
            onChange={(e) => onChange({ ...businessRules, cancellation_fee_amount: e.target.value })}
            data-testid="cancellation-fee-input" />
        </div>
      </div>
    </div>

    <div>
      <div className="flex items-center gap-1.5">
        <h2 className="font-heading text-lg font-semibold">Payment Enforcement</h2>
        <HelpHint text="If a student's balance stays unpaid past this grace period, scheduling and classroom registration are automatically put on hold until it's resolved or an office override is granted." testId="grace-period-help" />
      </div>
      <div className="max-w-xs mt-3 space-y-1.5">
        <Label>Grace period before an account is past due (days)</Label>
        <Input type="number" min={0} value={businessRules.grace_period_days}
          onChange={(e) => onChange({ ...businessRules, grace_period_days: e.target.value })}
          data-testid="grace-period-input" />
      </div>
    </div>

    <div>
      <div className="flex items-center gap-1.5">
        <h2 className="font-heading text-lg font-semibold">Reminders</h2>
        <HelpHint text="Turn automatic in-app lesson reminders on or off. Classroom reminders always stay on so families never miss parking/what-to-bring details." testId="reminders-help" />
      </div>
      <div className="space-y-2.5 mt-3 max-w-sm">
        <div className="flex items-center justify-between rounded-lg border p-2.5">
          <span className="text-sm">3-day-ahead lesson reminder</span>
          <Switch checked={businessRules.reminder_3day_enabled}
            onCheckedChange={(v) => onChange({ ...businessRules, reminder_3day_enabled: v })}
            data-testid="reminder-3day-switch" />
        </div>
        <div className="flex items-center justify-between rounded-lg border p-2.5">
          <span className="text-sm">1-day-ahead lesson reminder</span>
          <Switch checked={businessRules.reminder_1day_enabled}
            onCheckedChange={(v) => onChange({ ...businessRules, reminder_1day_enabled: v })}
            data-testid="reminder-1day-switch" />
        </div>
      </div>
    </div>

    <div>
      <div className="flex items-center gap-1.5">
        <h2 className="font-heading text-lg font-semibold">Training Requirements</h2>
        <HelpHint text="Drives the Student Progress Card on every Parent/Student dashboard: lessons/hours needed, road-test eligibility, and the completion forecast — update anytime as your program changes." testId="training-requirements-help" />
      </div>
      <div className="grid sm:grid-cols-3 gap-3 max-w-xl mt-3">
        <div className="space-y-1.5">
          <Label>Driving lessons required</Label>
          <Input type="number" min={0} value={businessRules.required_driving_lessons}
            onChange={(e) => onChange({ ...businessRules, required_driving_lessons: e.target.value })}
            data-testid="required-driving-lessons-input" />
        </div>
        <div className="space-y-1.5">
          <Label>Classroom hours required</Label>
          <Input type="number" min={0} value={businessRules.required_classroom_hours}
            onChange={(e) => onChange({ ...businessRules, required_classroom_hours: e.target.value })}
            data-testid="required-classroom-hours-input" />
        </div>
        <div className="space-y-1.5">
          <Label>Observation hours required</Label>
          <Input type="number" min={0} value={businessRules.required_observation_hours}
            onChange={(e) => onChange({ ...businessRules, required_observation_hours: e.target.value })}
            data-testid="required-observation-hours-input" />
        </div>
      </div>
    </div>

    <div>
      <div className="flex items-center gap-1.5">
        <h2 className="font-heading text-lg font-semibold">Business Hours</h2>
        <HelpHint text="Lessons can only be scheduled within these hours and on these days. Adjust anytime — no code changes needed." testId="business-hours-help" />
      </div>
      <div className="grid sm:grid-cols-2 gap-3 max-w-md mt-3">
        <div className="space-y-1.5">
          <Label>Start time</Label>
          <Input type="time" value={businessRules.business_start_time}
            onChange={(e) => onChange({ ...businessRules, business_start_time: e.target.value })}
            data-testid="business-start-time-input" />
        </div>
        <div className="space-y-1.5">
          <Label>End time</Label>
          <Input type="time" value={businessRules.business_end_time}
            onChange={(e) => onChange({ ...businessRules, business_end_time: e.target.value })}
            data-testid="business-end-time-input" />
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-3" data-testid="business-days-toggle-group">
        {WEEKDAYS.map((d) => {
          const active = (businessRules.business_days || []).includes(d.key);
          return (
            <button
              key={d.key}
              type="button"
              onClick={() => onToggleBusinessDay(d.key)}
              className={`h-9 px-3 rounded-[var(--radius)] text-sm font-medium border transition-colors ${
                active ? "bg-[hsl(var(--role-accent))] text-white border-transparent" : "bg-background text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`business-day-${d.key}`}
            >
              {d.label}
            </button>
          );
        })}
      </div>
    </div>

    <div className="pt-2 border-t">
      <Button onClick={onSave} disabled={saving} className="bg-[hsl(var(--role-accent))] hover:opacity-95" data-testid="business-rules-save-button">
        {saving ? "Saving..." : "Save Business Rules"}
      </Button>
    </div>
  </div>
);
